<template>
  <div class="uk-grid-medium content-management-page" uk-grid>
    <div class="uk-width-5-5">
      <div class="d-flex">
        <nav id="breadcrumbs" class="mb-3">
          <ul>
            <li>
              <router-link :to="{ name: 'company.dashboard' }">
                <i class="uil-home-alt"></i>
              </router-link>
            </li>
            <li> {{ $t('general.my_competency_approvals') }}</li>
          </ul>
        </nav>
      </div>

      <div class="card">
        <!-- Card header -->
        <div class="card-header actions-toolbar border-0">
          <div class="d-flex justify-content-between align-items-center">
            <div class="row" style="width:100%">
              <div class=" col-md-1  search-icon ">
                <i class="uil-search" />
              </div>
              <div class="col-md-11 uk-search uk-search-navbar" style="margin-left:-2rem">
                <input v-model="filter.search" :placeholder="$t('general.search')"
                  class="uk-search-input shadow-0 uk-form-small" type="search" @input="debouncedSearch" />
              </div>
            </div>
            <sort-dropdown :sort-options="sortOption" :title="sortTitle" @sort-selected="sortOrder" />
          </div>
        </div>

        <!-- Table -->
        <div class="table-responsive">
          <table class="table align-items-center">
            <thead>
              <tr>
                <th scope="col">{{ $t('general.user') }}</th>
                <th scope="col">{{ $t('general.competency_name') }}</th>
                <th scope="col">{{ $t('general.description') }}</th>
                <th scope="col">{{ $t('general.status') }}</th>
                <th scope="col">{{ $t('general.reviewers_count') }}</th>
                <th scope="col">{{ $t('general.created_at') }}</th>
                <th scope="col">{{ $t('general.transactions') }}</th>
              </tr>
            </thead>
            <tbody class="list">
              <tr v-for="(userContent, index) in userContents" :key="'user_contents_' + index">
                <td>
                  <div class="media align-items-center">
                    <UserAvatarImage v-if="userContent.user_photo_link" :photo="userContent.user_photo_link" />
                    <div class="media-body ml-4">
                      <a class="name h6 mb-0 text-sm disabled">{{
                        userContent.user_full_name
                      }}</a>
                      <small class="d-block font-weight-bold">{{ userContent.user_email }}</small>
                    </div>
                  </div>
                </td>
                <td>{{ userContent.competency_name }}</td>
                <td>{{ userContent.competency_description }}</td>
                <td class="status-text-result">
                  <span :class="getStatusClass(userContent.status)">{{
                    getStatusText(userContent.status)
                  }}</span>
                </td>
                <td>{{ userContent.reviewers_count }}</td>

                <td>
                  {{ formatDate(userContent.created_at) }}
                </td>
                <td class="text-right">
                  <div class="actions ml-3 d-flex flex-row">
                    <button class="btn btn-icon btn-hover btn-sm btn-circle" :uk-tooltip="$t('general.detail')"
                      @click="openDetailModal(userContent)">
                      <i class="uil-eye"></i>
                    </button>
                    <button v-if="$hasPermission('create_competency') && userContent.status === 'pending'"
                      class="btn btn-icon btn-hover btn-sm btn-circle" :uk-tooltip="$t('general.take_action')"
                      @click="openApprovalModal(userContent)">
                      <i class="uil-cog"></i>
                    </button>

                    <button v-else-if="userContent.status === 'pending'" class="btn btn-icon btn-hover btn-sm btn-circle"
                      disabled :style="{ cursor: 'not-allowed' }" :uk-tooltip="$t('general.no_permission')">
                      <i class="uil-cog"></i>
                    </button>

                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <pagination-tab v-if="pagination?.links" :pagination="pagination" @changePage="setPage($event)" />

        </div>
      </div>
    </div>

    <!-- Competency Detail Component -->
    <CompetencyDetail :competency="selectedCompetency" />

    <!-- Approval Modal Component -->
    <ApprovalModal :competency="selectedApprovalCompetency" :is-approving="isApproving" :is-rejecting="isRejecting"
      @approve="approveCompetency" @reject="rejectCompetency" />
  </div>
</template>

<script>
import DataTable from "@/assets/components/dataTable/DataTable";
import PaginationTab from "@/assets/components/Pagination";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";
import Pagination from "@/assets/components/Pagination.vue";
import UserAvatarImage from "@/view/components/image/user-avatar-image/user-avatar-image.vue";
import EventBus from "@/view/components/event-bus";
import CompetencyDetail from "./components/competency/CompetencyDetail.vue";
import ApprovalModal from "./components/competency/ApprovalModal.vue";

import _ from "lodash";
import axios from "axios";

import { showSuccess, showError } from "@/utils/alertUtils";
import moment from "moment-timezone";

export default {
  name: "Competencies",
  components: {
    UserAvatarImage,
    Pagination,
    DataTable,
    PaginationTab,
    SortDropdown,
    CompetencyDetail,
    ApprovalModal
  },
  data() {
    return {
      pagination: null,
      page: 1,
      userModal: null,
      filter: {
        search: null,
        order_by: "id",
        sort: 'desc'
      },
      userContents: [],
      selectedCompetency: null,
      selectedApprovalCompetency: null,
      isApproving: false,
      isRejecting: false,
      sortTitle: this.$t("filters.order.sort"),

      sortOption: [
        { key: 'created_at-asc', sort: 'asc', order: 'id', label: this.$t("sorting.created_at_asc") },
        { key: 'created_at-desc', sort: 'desc', order: 'id', label: this.$t("sorting.created_at_desc") },
      ],

    };
  },
  mounted() {
    this.fetchCompetenciesApproval()
  },
  methods: {
    setPage(page) {
      let self = this;
      self.page = page;
      this.fetchCompetenciesApproval();
    },

    sortOrder({ sort, order }) {
      let self = this;
      self.page = 1;
      self.filter.order_by = order;
      self.filter.sort = sort;

      this.fetchCompetenciesApproval()
    },

    async fetchCompetenciesApproval() {
      const { data } = await axios.get(`api/competencies/approval-management`, {
        params: {
          page: this.page,
          search: this.filter.search,
          sort: this.filter.sort,
          order_by: this.filter.order_by
        }
      })

      this.userContents = data?.data
      this.pagination = data?.meta

      await this.getUnProcessedCompetenciesCount()
    },

    async getUnProcessedCompetenciesCount() {
      const { data } = await axios.get(`api/competencies/approval-management`, {
        params: {
          count: true
        }
      })

      EventBus.$emit("competencyContentCount", data ?? 0);
    },

    debouncedSearch: _.debounce(function () {
      this.page = 1;
      this.fetchCompetenciesApproval();
    }, 500),

    openDetailModal(competency) {
      this.selectedCompetency = competency;
      UIkit.modal("#detail-modal").show();
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return moment(dateString).add(3, 'hours').format("DD/MM/YYYY HH:mm")
    },

    openApprovalModal(competency) {
      this.selectedApprovalCompetency = competency;
      UIkit.modal("#approval-modal").show();
    },

    async approveCompetency(competency) {
      if (!competency) return;

      this.isApproving = true;

      try {
        await axios.post(`api/competencies/${competency.id}/approval-management`, {
          status: 'approved'
        });

        showSuccess(this.$t("general.competency_approved_successfully"))

        // Modalı kapat
        UIkit.modal("#approval-modal").hide();

        // Listeyi yenile
        await this.fetchCompetenciesApproval();

      } catch (error) {
        console.error('Onaylama hatası:', error);
        showError(this.$t("general.approval_error"))
      } finally {
        this.isApproving = false;
      }
    },

    async rejectCompetency(competency) {
      if (!competency) return;

      this.isRejecting = true;

      try {
        await axios.post(`api/competencies/${competency.id}/approval-management`, {
          status: 'rejected'
        });

        showSuccess(this.$t("general.competency_rejected_successfully"))

        // Modalı kapat
        UIkit.modal("#approval-modal").hide();

        // Listeyi yenile
        await this.fetchCompetenciesApproval();

      } catch (error) {
        console.error('Reddetme hatası:', error);
        showError(this.$t("general.reject_error"))
      } finally {
        this.isRejecting = false;
      }
    },

    getStatusText(status) {
      switch (status) {
        case 'pending':
          return this.$t('general.pending');
        case 'approved':
          return this.$t('general.approved');
        case 'rejected':
          return this.$t('general.rejected');
        default:
          return '--';
      }
    },

    getStatusClass(status) {
      switch (status) {
        case 'pending':
          return 'pending';
        case 'approved':
          return 'approved';
        case 'rejected':
          return 'rejected';
        default:
          return '';
      }
    }
  },

};
</script>
<style lang="scss" scoped>
.content-management-page {
  .approve-text-result {
    .approved {
      color: #49D052;
    }

    .rejected {
      color: #ce2b2b;
    }

    .icon-feather-check {
      color: #49D052 !important;
      margin-right: 4px;
    }

    .icon-feather-x {
      color: #ce2b2b !important;
      margin-right: 4px;
    }
  }

  .status-text-result {
    .pending {
      color: #ffa500;
      font-weight: 500;
    }

    .approved {
      color: #49D052;
      font-weight: 500;
    }

    .rejected {
      color: #ce2b2b;
      font-weight: 500;
    }
  }

  // Approval Button Styles
  .approval-btn {
    background: #ffffff;
    border: 1px solid #667eea;
    color: #667eea;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 90px;
    justify-content: center;

    &:hover {
      background: #667eea;
      color: #ffffff;
      border-color: #667eea;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 1px 4px rgba(102, 126, 234, 0.2);
    }

    i {
      font-size: 12px;
    }

    .btn-text {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.3px;
    }
  }
}


// Responsive adjustments
@media (max-width: 768px) {
  // Responsive styles for approval modal can be added here if needed
}
</style>
